import React, { useEffect, useRef, useState } from "react";
import { UserRound } from "lucide-react";
import useAuthenticatedPhoto from "@/lib/useAuthenticatedPhoto";
import { api } from "@/lib/api";
import { prepareProfilePhoto } from "@/lib/prepareProfilePhoto";
import "./TotoIdentityCard.css";

/** Uses only the portrait supplied on the authorized employee/account record. */
export function IdentityPortrait({ picture, name }) {
  const [failedSource, setFailedSource] = useState(null);
  const source = useAuthenticatedPhoto(picture);
  return <div className="toto-identity-portrait">
    {source && source !== failedSource
      ? <img src={source} alt={name || "Profile portrait"} loading="lazy" decoding="async" onError={() => setFailedSource(source)} />
      : <div className="toto-identity-portrait__empty"><UserRound size={42} strokeWidth={1.2} aria-hidden="true" /><span>Photo not added</span></div>}
  </div>;
}

export function canManageEmployeePhoto(actor, employee) {
  return Boolean(actor && employee && (["owner", "coo", "recruiter", "onboarding_admin"].includes(actor.role) ||
    (actor.role === "boutique_manager" && actor.boutique_code === employee.boutique_code)));
}

export default function TotoIdentityCard({ person, name, title, children, className = "", headingLevel = 2, canEditPhoto = false, onPhotoSaved, portraitBeside = false }) {
  const inputRef = useRef(null), selection = useRef(0);
  const [pendingFile, setPendingFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [savedPicture, setSavedPicture] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [message, setMessage] = useState("");
  const picture = savedPicture || person?.picture;
  const hasPhoto = Boolean(picture && !picture.includes("pravatar.cc"));
  useEffect(() => {
    if (!pendingFile) { setPreview(""); return undefined; }
    const url = URL.createObjectURL(pendingFile);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [pendingFile]);
  useEffect(()=>()=>{selection.current++;},[]);
  const clearSelection = () => { selection.current++;setPendingFile(null); if (inputRef.current) inputRef.current.value = ""; };
  const choosePhoto = async (file) => {
    if (!file) return;
    const request=++selection.current;
    setMessage("");setPendingFile(null);setPreparing(true);
    try {
      const prepared=await prepareProfilePhoto(file);
      if(request!==selection.current)return;
      setPendingFile(prepared);
      if(prepared!==file)setMessage("Photo automatically prepared for upload. Original unchanged.");
    } catch(error) {
      if(request!==selection.current)return;
      setMessage(error.message||"This photo could not be prepared. Please choose another image.");
      if(inputRef.current)inputRef.current.value="";
    } finally {if(request===selection.current)setPreparing(false);}
  };
  const savePhoto = async () => {
    if (!pendingFile || busy || preparing || !canEditPhoto || !person?.user_id) return;
    setBusy(true); setMessage("");
    const body = new FormData(); body.append("file", pendingFile);
    try {
      const response = await api.post(`/employees/${encodeURIComponent(person.user_id)}/photo`, body);
      setSavedPicture(response.data.picture);
      clearSelection();
      setMessage("Photo saved.");
      await onPhotoSaved?.(response.data.picture);
    } catch (error) { setMessage(error.response?.status === 413 ? "The photo upload was rejected as too large. Choose a photo up to 2 MB and try again." : error.response?.data?.detail || "Photo could not be saved. Please try again."); }
    finally { setBusy(false); }
  };
  const Heading = `h${headingLevel}`;
  const details = <>
    <Heading className="toto-identity-card__name">{name || "Team member"}</Heading>
    <p className="toto-identity-card__role">{title || "Team member"}</p>
    {canEditPhoto && person?.user_id && <div className="toto-identity-card__photo-controls">
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" aria-label={`Choose photo for ${name || "your profile"}`} className="sr-only" onChange={(event) => choosePhoto(event.target.files?.[0])} disabled={busy || preparing} />
      {pendingFile ? <><p>Preview · {pendingFile.name}</p><div><button type="button" onClick={savePhoto} disabled={busy || preparing}>{busy ? "Saving…" : "Save photo"}</button><button type="button" onClick={() => { clearSelection(); setMessage(""); }} disabled={busy || preparing}>Cancel</button></div></>
        : <button type="button" disabled={preparing} onClick={() => inputRef.current?.click()}>{preparing ? "Preparing photo…" : hasPhoto ? "Change photo" : "Add photo"}</button>}
      <small>JPG, PNG or WebP · larger photos are sized automatically</small>
      {message && <p role="status">{message}</p>}
    </div>}
    {children}
  </>;
  return <article className={`toto-identity-card ${portraitBeside ? "toto-identity-card--portrait-beside" : ""} ${className}`.trim()}>
    <IdentityPortrait picture={preview || picture} name={name} />
    {portraitBeside ? <div className="toto-identity-card__details">{details}</div> : details}
  </article>;
}
